import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface RfsUnavailableBannerProps {
  visible: boolean;
}

/**
 * Inline banner (NOT a modal) shown when the RFS closes while SP is filling the offer form.
 * Slides down with animation when `visible` becomes true. Has amber left-bar accent.
 */
const RfsUnavailableBanner = ({ visible }: RfsUnavailableBannerProps) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:600px)");

  if (!visible) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: isPhone ? "flex-start" : "center",
        flexDirection: isPhone ? "column" : "row",
        gap: isPhone ? 1 : 1.5,
        background: "#FFFBEB",
        borderLeft: "4px solid #F59E0B",
        borderRadius: "8px",
        padding: "12px 16px",
        mb: 2,
        animation: "bannerSlideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1), bannerPulse 3s ease-in-out 0.4s infinite",
        "@keyframes bannerSlideDown": {
          "0%": { opacity: 0, transform: "translateY(-10px)", maxHeight: 0 },
          "100%": { opacity: 1, transform: "translateY(0)", maxHeight: "120px" },
        },
        "@keyframes bannerPulse": {
          "0%, 100%": { boxShadow: "0 0 0 0 rgba(245, 158, 11, 0)" },
          "50%": { boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.08)" },
        },
      }}
    >
      <WarningAmberOutlinedIcon
        sx={{ color: "#D97706", fontSize: "20px", flexShrink: 0, mt: isPhone ? 0 : "1px" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            color: "#92400E",
            lineHeight: 1.5,
            mb: 0.25,
          }}
        >
          {translate("raceCondition.rfsUnavailableTitle")}
        </Box>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 400,
            color: "#A16207",
            lineHeight: 1.5,
          }}
        >
          {translate("raceCondition.rfsUnavailableBody")}
        </Box>
      </Box>
      <Box
        component="span"
        onClick={() => navigate("/service-board")}
        sx={{
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 600,
          color: "#D97706",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          "&:hover": { textDecoration: "underline", color: "#B45309" },
        }}
      >
        {translate("raceCondition.returnToServiceBoard")}
      </Box>
    </Box>
  );
};

export default RfsUnavailableBanner;
